import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { resetStore } from "./resetStore"
import { clearJwtToken } from "../../Utils/authStorage"
import { apiRequest } from "../../Utils/apiClient"

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback

const unwrapResponse = (response) => response?.data || response || {}

export const fetchAdditionalAppointmentReminder = createAsyncThunk(
  "additionalAppointmentReminders/fetchOne",
  async ({ uid, appointmentId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `/appointment-additional-reminders/${appointmentId}?uid=${encodeURIComponent(uid)}`
      )
      const data = unwrapResponse(response)

      return {
        appointmentId,
        reminder: data?.reminder || null,
      }
    } catch (error) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken()
        } catch {}
      }

      return rejectWithValue(
        getError(error, "fetchAdditionalAppointmentReminder failed")
      )
    }
  }
)

export const saveAdditionalAppointmentReminder = createAsyncThunk(
  "additionalAppointmentReminders/save",
  async (
    { uid, appointmentId, minutesBefore },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(
        "PUT",
        `/appointment-additional-reminders/${appointmentId}`,
        { uid, minutesBefore }
      )
      const data = unwrapResponse(response)

      return {
        appointmentId,
        reminder: data.reminder,
      }
    } catch (error) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken()
        } catch {}
      }

      return rejectWithValue(
        getError(error, "saveAdditionalAppointmentReminder failed")
      )
    }
  }
)

export const deleteAdditionalAppointmentReminder = createAsyncThunk(
  "additionalAppointmentReminders/delete",
  async ({ uid, appointmentId }, { rejectWithValue }) => {
    try {
      await apiRequest(
        "DELETE",
        `/appointment-additional-reminders/${appointmentId}?uid=${encodeURIComponent(uid)}`
      )

      return { appointmentId }
    } catch (error) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken()
        } catch {}
      }

      return rejectWithValue(
        getError(error, "deleteAdditionalAppointmentReminder failed")
      )
    }
  }
)

const initialState = {
  byAppointmentId: {},
  statusByAppointmentId: {},
  errorByAppointmentId: {},
}

const additionalAppointmentRemindersSlice = createSlice({
  name: "additionalAppointmentReminders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const setPending = (state, action) => {
      const appointmentId = String(action.meta.arg.appointmentId)
      state.statusByAppointmentId[appointmentId] = "loading"
      state.errorByAppointmentId[appointmentId] = null
    }

    const setRejected = (state, action) => {
      const appointmentId = String(action.meta.arg.appointmentId)
      state.statusByAppointmentId[appointmentId] = "failed"
      state.errorByAppointmentId[appointmentId] = action.payload
    }

    builder
      .addCase(fetchAdditionalAppointmentReminder.pending, setPending)
      .addCase(fetchAdditionalAppointmentReminder.fulfilled, (state, action) => {
        const appointmentId = String(action.payload.appointmentId)
        state.statusByAppointmentId[appointmentId] = "succeeded"
        state.byAppointmentId[appointmentId] = action.payload.reminder
      })
      .addCase(fetchAdditionalAppointmentReminder.rejected, setRejected)
      .addCase(saveAdditionalAppointmentReminder.pending, setPending)
      .addCase(saveAdditionalAppointmentReminder.fulfilled, (state, action) => {
        const appointmentId = String(action.payload.appointmentId)
        state.statusByAppointmentId[appointmentId] = "succeeded"
        state.byAppointmentId[appointmentId] = action.payload.reminder
      })
      .addCase(saveAdditionalAppointmentReminder.rejected, setRejected)
      .addCase(deleteAdditionalAppointmentReminder.pending, setPending)
      .addCase(deleteAdditionalAppointmentReminder.fulfilled, (state, action) => {
        const appointmentId = String(action.payload.appointmentId)
        state.statusByAppointmentId[appointmentId] = "succeeded"
        state.byAppointmentId[appointmentId] = null
      })
      .addCase(deleteAdditionalAppointmentReminder.rejected, setRejected)
      .addCase(resetStore, () => initialState)
  },
})

export default additionalAppointmentRemindersSlice.reducer